/* eslint-disable indent */
import { InfoCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tooltip,
  Typography
} from "antd";
import { useState } from "react";

import { PAYMENTS_DISCOUNT_MANAGEMENT } from "../../../constants/routes";
import { createSlug } from "../../../utils/helper.jsx";
import {
  TYPE_TARGET_FIELD_NAME,
  WAITLIST_TYPE_DESCRIPTIONS,
  WAITLIST_TYPE_LABELS,
  WAITLIST_TYPES
} from "../constants";
import { getTypeTargetFieldValue, isDispatchableType } from "../helper";

const { Title, Text, Link } = Typography;
const { Option } = Select;
const { Item } = Form;

const PLATFORM_DEFAULT_SENDER_VALUE = "PLATFORM_DEFAULT";
const MERGE_TAG_TOOLTIP =
  "Supported template variables, in the subject and the body: {{name}} and {{email}}. " +
  '{{name}} becomes "there" when the email has no account.';

// Opens the resource's own admin page in a new tab so the target select can be re-fetched without losing form state.
const QuickCreateLink = ({ href, label }) => (
  <Link onClick={() => window.open(href, "_blank")} style={{ fontSize: 12, marginLeft: 8 }}>
    {label}
  </Link>
);

// ─── Section header ────────────────────────────────────────────────────────────

const SectionHeader = ({ title, style = {} }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 16,
      marginTop: 4,
      ...style
    }}
  >
    <Title level={5} style={{ margin: 0 }}>
      {title}
    </Title>
  </div>
);

// ─── CreateEditConfigModal ─────────────────────────────────────────────────────

const CreateEditConfigModal = ({
  isModalOpen = false,
  isEdit = false,
  isSubmitting = false,
  initialValues = {},
  formOptions: waitlistFormOptions = {},
  setIsModalOpen = () => {},
  handleSubmit = () => {},
  refreshFormOptions = () => {},
  isRefreshingFormOptions = false
}) => {
  const [form] = Form.useForm();
  const type = Form.useWatch("type", form);
  const sendJoiningConfirmationEmail = Form.useWatch("sendJoiningConfirmationEmail", form);
  const sendOnDispatchEmail = Form.useWatch("sendOnDispatchEmail", form);
  const [uniquePublicIdManuallyEdited, setUniquePublicIdManuallyEdited] = useState(false);

  // On-dispatch email only applies to types that actually run a dispatch.
  const dispatchable = isDispatchableType(type);

  const {
    discountCouponOptions = [],
    accessKeyOptions = [],
    senderOptions = []
  } = waitlistFormOptions;

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Auto-slug the Unique Public ID from Name while creating — stops once the admin edits the ID by hand.
  const handleValuesChange = (changedValues) => {
    if (!isEdit && "name" in changedValues && !uniquePublicIdManuallyEdited) {
      form.setFieldsValue({ uniquePublicId: createSlug(changedValues.name) });
    }
    // Each type has its own target field semantics (coupon/access key/mailing list) — a value picked
    // for the previous type is never valid for the new one, so it must not survive the switch.
    if ("type" in changedValues) {
      form.setFieldsValue({ targetFieldValue: undefined });
    }
  };

  const handleFormSubmit = (values) => {
    const {
      sendJoiningConfirmationEmail,
      senderId,
      subject,
      htmlBody,
      sendOnDispatchEmail,
      dispatchSenderId,
      dispatchSubject,
      dispatchHtmlBody,
      targetFieldValue,
      type,
      uniquePublicId,
      ...rest
    } = values;

    // The platform-default option is a UI-only sentinel — map it back to null so the
    // backend falls back to its own default sender instead of a specific one.
    const toBulkSenderId = (value) =>
      value === PLATFORM_DEFAULT_SENDER_VALUE ? null : value;

    const joiningConfirmationEmail = sendJoiningConfirmationEmail
      ? { subject, htmlBody, bulkNotificationSenderId: toBulkSenderId(senderId) }
      : null;

    // On-dispatch email is only meaningful for dispatchable types.
    const onDispatchEmail =
      isDispatchableType(type) && sendOnDispatchEmail
        ? {
            subject: dispatchSubject,
            htmlBody: dispatchHtmlBody,
            bulkNotificationSenderId: toBulkSenderId(dispatchSenderId)
          }
        : null;

    const payload = { ...rest, joiningConfirmationEmail, onDispatchEmail };

    // GENERAL_WAITLIST has no type-specific target field.
    const targetFieldName = TYPE_TARGET_FIELD_NAME[type];
    if (targetFieldName) {
      payload[targetFieldName] = targetFieldValue;
    }

    // type and uniquePublicId are immutable after creation — only send them on create.
    if (!isEdit) {
      payload.type = type;
      payload.uniquePublicId = uniquePublicId;
    }

    handleSubmit(payload);
  };

  const formInitialValues = isEdit
    ? (() => {
        const {
          name,
          uniquePublicId,
          description,
          type,
          isActive,
          joiningConfirmationEmail,
          onDispatchEmail
        } = initialValues;
        const { subject, htmlBody, bulkNotificationSenderId } = joiningConfirmationEmail || {};
        const {
          subject: dispatchSubject,
          htmlBody: dispatchHtmlBody,
          bulkNotificationSenderId: dispatchBulkSenderId
        } = onDispatchEmail || {};
        return {
          name,
          uniquePublicId,
          description,
          type,
          isActive,
          sendJoiningConfirmationEmail: Boolean(joiningConfirmationEmail),
          subject,
          htmlBody,
          senderId: bulkNotificationSenderId ?? PLATFORM_DEFAULT_SENDER_VALUE,
          sendOnDispatchEmail: Boolean(onDispatchEmail),
          dispatchSubject,
          dispatchHtmlBody,
          dispatchSenderId: dispatchBulkSenderId ?? PLATFORM_DEFAULT_SENDER_VALUE,
          targetFieldValue: getTypeTargetFieldValue(initialValues)
        };
      })()
    : { isActive: true, sendJoiningConfirmationEmail: false, sendOnDispatchEmail: false };

  const renderTypeTargetField = () => {
    switch (type) {
      case WAITLIST_TYPES.DISCOUNT_ALLOWLIST:
        return (
          <Item
            name="targetFieldValue"
            label={
              <Space size={0} style={{ width: "100%" }}>
                <span>Discount Coupon</span>
                <QuickCreateLink href={PAYMENTS_DISCOUNT_MANAGEMENT} label="Create a coupon" />
                <Tooltip title="Refresh options">
                  <Button
                    type="text"
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={refreshFormOptions}
                    loading={isRefreshingFormOptions}
                  />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: "Please select a discount coupon!" }]}
          >
            <Select
              placeholder="Select the private discount to allowlist"
              options={discountCouponOptions}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Item>
        );
      case WAITLIST_TYPES.USER_ACCESS_GRANT:
        return (
          <Item
            name="targetFieldValue"
            label={
              <Space size={0} style={{ width: "100%" }}>
                <span>Access Key</span>
                <Tooltip title="Refresh options">
                  <Button
                    type="text"
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={refreshFormOptions}
                    loading={isRefreshingFormOptions}
                  />
                </Tooltip>
              </Space>
            }
            tooltip="These are platform access flags set on a user's account (user.accessConfig). Granting one gives every waitlisted user that access when dispatched."
            rules={[{ required: true, message: "Please select an access key!" }]}
          >
            <Select
              placeholder="Select the accessConfig flag to grant"
              options={accessKeyOptions}
              optionRender={({ value, label, data: { description } }) => (
                <div>
                  <div>{label}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {description ?? value}
                  </Text>
                </div>
              )}
            />
          </Item>
        );
      default:
        // GENERAL_WAITLIST has no type-specific target field.
        return null;
    }
  };

  return (
    <Modal
      title={isEdit ? "Edit Waitlist" : "Create Waitlist"}
      open={isModalOpen}
      footer={null}
      onCancel={handleCancel}
      width={750}
      destroyOnClose
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={formInitialValues}
        name="waitlist_config_create_edit_form"
        onFinish={handleFormSubmit}
        onValuesChange={handleValuesChange}
      >
        {/* ── Section: Basic Information ── */}
        <SectionHeader title="Basic Information" />

        <Row gutter={16}>
          <Col span={12}>
            <Item
              name="name"
              label="Name"
              rules={[{ required: true, message: "Please enter a name!" }]}
            >
              <Input placeholder="Prime Early-Bird Sale" />
            </Item>
          </Col>
          <Col span={12}>
            <Item
              name="uniquePublicId"
              label="Unique Public ID"
              rules={[{ required: true, message: "Please enter a unique public ID!" }]}
            >
              <Input
                placeholder="prime-early-bird-sale"
                readOnly={isEdit}
                disabled={isEdit}
                onChange={({ target: { value } }) => {
                  setUniquePublicIdManuallyEdited(true);
                  // Keep a hand-typed ID in the same slug shape the auto-fill produces, so the
                  // public URL and the list search never have to deal with mixed case.
                  form.setFieldsValue({ uniquePublicId: createSlug(value) });
                }}
              />
            </Item>
          </Col>
        </Row>

        <Item name="description" label="Description">
          <Input.TextArea
            rows={2}
            placeholder="Shown to users on the public join page (optional)."
            maxLength={500}
            showCount
          />
        </Item>

        <Row gutter={16}>
          <Col span={12}>
            <Item
              name="type"
              label="Type"
              tooltip={Object.values(WAITLIST_TYPES)
                .map(
                  (value) => `${WAITLIST_TYPE_LABELS[value]}: ${WAITLIST_TYPE_DESCRIPTIONS[value]}`
                )
                .join(" ")}
              rules={[{ required: true, message: "Please select a type!" }]}
            >
              <Select disabled={isEdit} placeholder="Select waitlist type">
                {Object.values(WAITLIST_TYPES).map((value) => (
                  <Option key={value} value={value}>
                    {WAITLIST_TYPE_LABELS[value]}
                  </Option>
                ))}
              </Select>
            </Item>
          </Col>
          <Col span={12}>{renderTypeTargetField()}</Col>
        </Row>

        {/* Type description callout — shown after a type is selected */}
        {type && (
          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            message={WAITLIST_TYPE_DESCRIPTIONS[type]}
            style={{ marginTop: -8, marginBottom: 16, fontSize: 13 }}
          />
        )}

        <Form.Item style={{ marginBottom: 12 }}>
          <Space align="center" size={10}>
            <Text>Status</Text>
            <Form.Item name="isActive" valuePropName="checked" noStyle>
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Space>
        </Form.Item>

        {/* ── Section: Joining Confirmation Email ── */}
        <Divider dashed style={{ margin: "12px 0 16px" }} />
        <SectionHeader title="Joining Confirmation Email" />

        <Form.Item style={{ marginBottom: 12 }}>
          <Space align="center" size={10}>
            <Text>Send confirmation email on join</Text>
            <Form.Item name="sendJoiningConfirmationEmail" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
          </Space>
        </Form.Item>

        {sendJoiningConfirmationEmail && (
          <div
            style={{
              background: "rgba(0,0,0,0.02)",
              border: "1px solid rgba(0,0,0,0.06)",
              borderRadius: 8,
              padding: "16px 16px 0",
              marginTop: 8,
              marginBottom: 8
            }}
          >
            <Item
              name="subject"
              label="Subject"
              rules={[{ required: true, message: "Please enter an email subject!" }]}
            >
              <Input placeholder="You're on the list!" />
            </Item>

            <Item
              name="htmlBody"
              label="HTML Body"
              tooltip={MERGE_TAG_TOOLTIP}
              rules={[{ required: true, message: "Please enter the email body!" }]}
            >
              <Input.TextArea rows={4} placeholder="<p>Hi {{name}}, you're on the waitlist!</p>" />
            </Item>

            <Item name="senderId" label="Sender">
              <Select>
                <Option value={PLATFORM_DEFAULT_SENDER_VALUE}>Platform default</Option>
                {senderOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Item>
          </div>
        )}

        {/* ── Section: On-Dispatch Email (dispatchable types only) ── */}
        {dispatchable && (
          <>
            <Divider dashed style={{ margin: "12px 0 16px" }} />
            <SectionHeader title="On-Dispatch Email" />

            <Form.Item style={{ marginBottom: 4 }}>
              <Space align="center" size={10}>
                <Text>Send email when a member is dispatched</Text>
                <Form.Item name="sendOnDispatchEmail" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </Space>
            </Form.Item>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
              Sent to each member the moment their dispatch completes — e.g. &quot;your discount
              coupon is ready.&quot;
            </Text>

            {sendOnDispatchEmail && (
              <div
                style={{
                  background: "rgba(0,0,0,0.02)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 8,
                  padding: "16px 16px 0",
                  marginTop: 8,
                  marginBottom: 8
                }}
              >
                <Item
                  name="dispatchSubject"
                  label="Subject"
                  rules={[{ required: true, message: "Please enter an email subject!" }]}
                >
                  <Input placeholder="Your early-bird discount is ready!" />
                </Item>

                <Item
                  name="dispatchHtmlBody"
                  label="HTML Body"
                  tooltip={MERGE_TAG_TOOLTIP}
                  rules={[{ required: true, message: "Please enter the email body!" }]}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder="<p>Hi {{name}}, your coupon is now active!</p>"
                  />
                </Item>

                <Item name="dispatchSenderId" label="Sender">
                  <Select>
                    <Option value={PLATFORM_DEFAULT_SENDER_VALUE}>Platform default</Option>
                    {senderOptions.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Item>
              </div>
            )}
          </>
        )}

        <Divider style={{ margin: "16px 0" }} />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isSubmitting}>
            {isEdit ? "Update Waitlist" : "Create Waitlist"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateEditConfigModal;
